import React, { useState, useMemo } from 'react';
import { HelpCircle, Video, X } from 'lucide-react'; // Added X for closing video modal
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose, // Import DialogClose
} from './dialog';
import guidesData from '../../help/guides.json';
// Import the utility function
import { getYouTubeVideoId } from '../../utils/youtubeUtils';

const HelpIcon = ({ sectionKey }) => {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false); // State for video modal

  const guide = guidesData[sectionKey];
  // Use the imported function
  const videoId = useMemo(() => getYouTubeVideoId(guide?.youtubeLink), [guide?.youtubeLink]);

  if (!guide) {
    console.warn(`Help guide not found for section: ${sectionKey}`);
    return null;
  }

  const videoEmbedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;

  return (
    <div className="flex items-center space-x-1"> {/* Reduced space */}
      {/* Help Text Modal */}
      <Dialog open={isHelpModalOpen} onOpenChange={setIsHelpModalOpen}>
        <DialogTrigger asChild>
          {/* Apply theme classes to trigger button */}
          <Button variant="ghost" size="icon" className="text-accent-foreground hover:text-accent-foreground/80 hover:bg-accent h-8 w-8"> {/* Smaller icon button */}
            <HelpCircle className="h-5 w-5" />
            <span className="sr-only">Show Help Guide</span>
          </Button>
        </DialogTrigger>
        {/* Apply theme classes to DialogContent */}
        <DialogContent className="sm:max-w-[600px] app-card-bg border-border text-primary max-h-[85vh] flex flex-col"> {/* Increased max-h slightly */}
          <DialogHeader>
            {/* Apply theme classes */}
            <DialogTitle className="text-accent-foreground text-xl">{guide.title}</DialogTitle>
            <DialogDescription className="text-muted-foreground pt-1">
              Step-by-step guide for this section.
            </DialogDescription>
          </DialogHeader>
          {/* Apply theme classes */}
          <div className="py-4 space-y-4 overflow-y-auto flex-grow pr-6">
            {/* Steps */}
            {guide.steps && guide.steps.length > 0 && (
              <div>
                {/* Apply theme class */}
                <h4 className="font-semibold text-primary mb-2">Steps:</h4>
                <ol className="list-decimal list-inside space-y-3">
                  {guide.steps.map((step) => (
                    // Apply theme classes
                    <li key={step.step} className="text-muted-foreground">
                      <strong className="text-primary">{step.title}:</strong> {step.description}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {/* Tips */}
            {guide.tips && guide.tips.length > 0 && (
              // Apply theme classes
              <div className="pt-4 border-t border-border/50 mt-4">
                <h4 className="font-semibold text-primary mb-2">Tips:</h4>
                <ul className="list-disc list-inside space-y-2">
                  {guide.tips.map((tip, index) => (
                    // Apply theme class
                    <li key={index} className="text-muted-foreground">{tip}</li>
                  ))}
                </ul>
              </div>
            )}
            {/* Embedded Video Section */}
            {videoEmbedUrl && (
              // Apply theme classes
              <div className="pt-4 border-t border-border/50 mt-4">
                <h4 className="font-semibold text-primary mb-2">Video Guide:</h4>
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={videoEmbedUrl}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded"
                  ></iframe>
                </div>
              </div>
            )}
          </div>
          {/* Apply theme classes */}
          <DialogFooter className="mt-auto pt-4 border-t border-border/50">
            {/* Change button style to solid purple */}
            <Button 
              onClick={() => setIsHelpModalOpen(false)} 
              // Remove variant="outline" and old classes
              // Add classes for solid purple button
              className="app-accent-purple-bg hover:app-accent-purple-hover text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Icon Button (opens separate video modal) */}
      {videoId && (
        <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
          <DialogTrigger asChild>
            {/* Keep red color for video icon, adjust hover */}
            <Button
              variant="ghost"
              size="icon"
              className="text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 h-8 w-8" // Smaller icon button
              title="Watch Video Guide"
            >
              <Video className="h-5 w-5" />
              <span className="sr-only">Watch Video Guide</span>
            </Button>
          </DialogTrigger>
          {/* Apply theme classes to video DialogContent */}
          <DialogContent className="sm:max-w-[800px] w-[90vw] app-bg-secondary border-border text-primary p-0"> {/* Use secondary bg, remove padding */}
             <div className="aspect-video relative">
               <iframe
                 width="100%"
                 height="100%"
                 src={videoEmbedUrl}
                 title="YouTube video player"
                 frameBorder="0"
                 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                 allowFullScreen
                 className="absolute top-0 left-0 w-full h-full"
               ></iframe>
             </div>
             {/* Optional: Add a close button overlay if needed, ensure it contrasts */}
             <DialogClose asChild>
                {/* Adjust close button for theme */}
                <Button variant="ghost" size="icon" className="absolute top-2 right-2 text-primary bg-background/50 hover:bg-background/70 rounded-full h-8 w-8">
                    <X className="h-5 w-5" />
                    <span className="sr-only">Close video</span>
                </Button>
             </DialogClose>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default HelpIcon;
